import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import withStyles, {
  WithStyles,
  StyleRulesCallback
} from '@material-ui/core/styles/withStyles';

import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

import { LinkListItem, InviteDialog } from '../components';

import FarmExploreView from './FarmExploreView';

import { FarmProvider, LayoutToolsProvider, LayoutTools } from '../providers';

import {
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  ListSubheader,
  Typography,
  Switch,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Checkbox,
  Snackbar,
  CircularProgress
} from '@material-ui/core';

import ReportsIcon from '@material-ui/icons/LibraryBooks';
import PermMediaIcon from '@material-ui/icons/PermMedia';
import InviteIcon from '@material-ui/icons/GroupAdd';
import EditIcon from '@material-ui/icons/Edit';
import ViewListIcon from '@material-ui/icons/ViewList';
import SettingsIcon from '@material-ui/icons/SettingsApplications';
import VisibilityIcon from '@material-ui/icons/Visibility';
import AssignmentIcon from '@material-ui/icons/Assignment';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import SidePanel from './SidePanel';

interface Renders {
  layout: LayoutTools;
  farmLoading: boolean;
  farm: Farm | null;
  selectedField: Field | null;
}

interface State {
  selectedField: Field | null;
  useMapView: boolean;
  compareMode: boolean;
  showInviteDialog: boolean;
}

const styles: StyleRulesCallback = theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    height: '100vh'
  },
  tablePaper: {
    padding: theme.spacing.unit * 3,
    flex: 1,
    overflowY: 'scroll',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'center'
  },
  tableHead: {
    backgroundColor: theme.palette.grey['200']
  },
  list: {
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing.unit * 2,
    marginTop: 20
  },
  compareContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  map: {
    flex: 1
  }
});

type Props = WithStyles & RouteComponentProps<any>;

class FarmExplore extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      useMapView: true,
      compareMode: false,
      selectedField: null,
      showInviteDialog: false
    };
  }

  renderLoading(message?: string | null) {
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={true}
        message={
          <>
            <CircularProgress color="secondary" size={25} />
            <span>{message || 'cargando'}</span>
          </>
        }
      />
    );
  }

  renderCompareView(farm: Farm) {
    const { classes } = this.props;
    return (
      <div className={classes.compareContainer}>
        <FarmExploreView
          className={classes.map}
          farm={farm}
          onFieldClick={args => this.setState({ selectedField: args.field })}
        />
        <FarmExploreView
          className={classes.map}
          farm={farm}
          onFieldClick={args => this.setState({ selectedField: args.field })}
        />
      </div>
    );
  }

  renderMapView(farm: Farm) {
    const { classes } = this.props;
    return (
      <FarmExploreView
        className={classes.map}
        farm={farm}
        onFieldClick={args => this.setState({ selectedField: args.field })}
      />
    );
  }

  renderTableView(farm: Farm, selectedField: Field | null) {
    const { classes } = this.props;
    return (
      <Paper square={true} elevation={2} className={classes.tablePaper}>
        <Table>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell>Seleccionar</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Cultivo Actual</TableCell>
              <TableCell numeric={true}>Area</TableCell>
              <TableCell numeric={true}>Perimetro</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {farm.fields.map(f => {
              return (
                <TableRow key={f.id} hover={true}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={!!(selectedField && f.id === selectedField.id)}
                      onChange={() => this.setState({ selectedField: f })}
                    />
                  </TableCell>
                  <TableCell>{f.name}</TableCell>
                  <TableCell>{f.currentCrop}</TableCell>
                  <TableCell numeric={true}>{f.area || 'xxx ha'}</TableCell>
                  <TableCell numeric={true}>{'xxx km'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  renderFarmMenu(provs: Renders) {
    const {
      match: {
        params: { farmId }
      }
    } = this.props;

    return (
      <List>
        <LinkListItem button={true} to={`/newfield/${farmId}`}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText primary="Definir Lotes" />
        </LinkListItem>
        <Divider />
        <ListItem>
          <ListItemIcon>
            <ViewListIcon />
          </ListItemIcon>
          <ListItemText primary="Formato Tabla" />
          <ListItemSecondaryAction>
            <Switch
              onChange={() =>
                this.setState({
                  useMapView: !this.state.useMapView
                })
              }
            />
          </ListItemSecondaryAction>
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemIcon>
            <ViewListIcon />
          </ListItemIcon>
          <ListItemText primary="Comparar Vistas" />
          <ListItemSecondaryAction>
            <Switch
              onChange={() =>
                this.setState({
                  compareMode: !this.state.compareMode
                })
              }
            />
          </ListItemSecondaryAction>
        </ListItem>
        <Divider />
        <LinkListItem button={true} to={`/library/${farmId}`}>
          <ListItemIcon>
            <PermMediaIcon />
          </ListItemIcon>
          <ListItemText primary="Biblioteca" />
        </LinkListItem>
        <Divider />
        <LinkListItem button={true} to={`/reports/${farmId}/list`}>
          <ListItemIcon>
            <ReportsIcon />
          </ListItemIcon>
          <ListItemText primary="Reportes" />
        </LinkListItem>
        <Divider />
        <ListItem
          button={true}
          onClick={() => this.setState({ showInviteDialog: true })}
        >
          <ListItemIcon>
            <InviteIcon />
          </ListItemIcon>
          <ListItemText primary="Invitar Usuario" />
        </ListItem>
        <Divider />

        <LinkListItem button={true} to={`/config/${farmId}`}>
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary="Configuracion" />
        </LinkListItem>
      </List>
    );
  }

  renderFieldMenu(field: Field, farm: Farm, layout: LayoutTools) {
    const { classes } = this.props;

    return (
      <List
        dense={true}
        className={classes.list}
        subheader={<ListSubheader>Lote {field.name}</ListSubheader>}
      >
        <LinkListItem button={true} to={`/zones/${field.id}`}>
          <ListItemIcon>
            <TrendingUpIcon />
          </ListItemIcon>
          <ListItemText primary="Zonas de Gestión" />
        </LinkListItem>
        <Divider />
        <LinkListItem button={true} to={`/yield/${field.id}`}>
          <ListItemIcon>
            <TrendingUpIcon />
          </ListItemIcon>
          <ListItemText primary="Analisis de Productividad" />
        </LinkListItem>
        <Divider />
        <LinkListItem button={true} to={`/tracking/${field.id}`}>
          <ListItemIcon>
            <VisibilityIcon />
          </ListItemIcon>
          <ListItemText primary="Seguimiento Automatico" />
        </LinkListItem>
        <Divider />
        <LinkListItem button={true} to={`/prescription/${field.id}`}>
          <ListItemIcon>
            <AssignmentIcon />
          </ListItemIcon>
          <ListItemText primary="Generar Prescripcion" />
        </LinkListItem>
        <Divider />
        <ListItem button={true} onClick={() => layout.showBetaDialog()}>
          <ListItemIcon>
            <CloudDownloadIcon />
          </ListItemIcon>
          <ListItemText primary="Exportar Datos" />
        </ListItem>
      </List>
    );
  }

  composeProviders(children: (renders: Renders) => React.ReactNode) {
    const {
      match: {
        params: { farmId }
      }
    } = this.props;

    const { selectedField } = this.state;

    return (
      <LayoutToolsProvider>
        {layout => (
          <FarmProvider farmId={farmId} includeFields={true}>
            {(farm, farmLoading) =>
              children({
                layout,
                farmLoading: farmLoading || false,
                farm,
                selectedField
              })
            }
          </FarmProvider>
        )}
      </LayoutToolsProvider>
    );
  }

  render() {
    const {
      classes,
      match: {
        params: { farmId }
      }
    } = this.props;

    const { useMapView, compareMode, selectedField } = this.state;

    return this.composeProviders(provs => (
      <main className={classes.root}>
        {provs.farmLoading && this.renderLoading('cargando campo')}

        <InviteDialog
          farmId={farmId}
          show={this.state.showInviteDialog}
          onDismiss={() => this.setState({ showInviteDialog: false })}
        />

        <SidePanel>
          <Typography variant="subheading">Campo</Typography>
          <Typography variant="headline">
            {provs.farm ? provs.farm.name : ''}
          </Typography>

          {this.renderFarmMenu(provs)}

          {selectedField &&
            provs.farm &&
            this.renderFieldMenu(selectedField, provs.farm, provs.layout)}
        </SidePanel>

        {useMapView &&
          !compareMode &&
          provs.farm &&
          this.renderMapView(provs.farm)}
        {!useMapView &&
          provs.farm &&
          this.renderTableView(provs.farm, selectedField)}
        {useMapView &&
          compareMode &&
          provs.farm &&
          this.renderCompareView(provs.farm)}
      </main>
    ));
  }
}

export default withStyles(styles)(withRouter(FarmExplore));



// WEBPACK FOOTER //
// ./src/containers/FarmExplore.tsx